/**
 * CP-246: "my alerts" for THIS browser.
 *
 * The server no longer lists alerts for an email address (anyone could type
 * any address). When someone creates an alert the response carries a cancel
 * token for that one alert; we keep it here, in this browser only, so
 * /manage-alerts can show and cancel what this browser created.
 * Alert emails carry their own cancel link.
 *
 * CP-246 Step 2: the emailed manage link is /manage-alerts#t=<token>. The token
 * lives in the URL fragment (never sent to a server or logged), is moved into
 * sessionStorage for this tab, and the fragment is stripped from the address bar.
 *
 * Plain functions only (no imports) so the node test can load this file.
 * `storage` is window.localStorage (or a stub); every call tolerates a missing
 * or throwing storage (private mode, quota).
 */
export const MY_ALERTS_KEY = 'cs-my-alerts';
export const MY_ALERTS_MAX = 50;
// Cancel tokens expire after 90 days on the server.
export const MY_ALERTS_MAX_AGE_MS = 90 * 24 * 3600 * 1000;
export const MANAGE_NEUTRAL_MESSAGE = "We'll email you a manage link.";
export const MANAGE_BROWSER_NOTE =
  'Alerts you set up in this browser are listed below. Every alert email also has its own cancel link.';
export const MANAGE_EMPTY_MESSAGE = 'No price alerts have been set up in this browser.';
export const MANAGE_TOKEN_KEY = 'cs-manage-token';
export const MANAGE_LINK_SENT_MESSAGE =
  "If that address has active price alerts, we've emailed it a manage link. The link works for 7 days.";
export const MANAGE_LINK_EXPIRED_MESSAGE =
  'This manage link is invalid or has expired. Enter your email to get a new one.';
// CP-246 #240 UI/UX round 1: one copy source for the Manage Alerts page.
export const MANAGE_SUBTITLE = 'Your active price alerts. Cancel any you no longer want.';
export const MANAGE_LOADING_MESSAGE = 'Loading your alerts\u2026';
export const MANAGE_MANAGED_EMPTY_MESSAGE = 'You have no active price alerts.';
export const MANAGE_LOAD_FAILED_MESSAGE = "We couldn't load your alerts.";
export const MANAGE_RATE_LIMIT_MESSAGE = 'Too many requests. Please try again in a few minutes.';
export const MANAGE_GENERIC_ERROR = 'Something went wrong. Please try again.';
export const MANAGE_SENDING_LABEL = 'Sending\u2026';
export const MANAGE_CANCELLING_LABEL = 'Cancelling\u2026';

/**
 * The message to show for a failed request on Manage Alerts. 429 is always the one rate-limit line;
 * the API client's generic "An error occurred" / "An unexpected error occurred" never reach the user.
 */
export function manageErrorMessage(err, fallback) {
  const status = err && err.response ? err.response.status : undefined;
  if (status === 429) return MANAGE_RATE_LIMIT_MESSAGE;
  const msg = err && typeof err.userMessage === 'string' ? err.userMessage.trim() : '';
  if (/^an (unexpected )?error occurred\.?$/i.test(msg)) return MANAGE_GENERIC_ERROR;
  return msg || fallback || MANAGE_GENERIC_ERROR;
}

/** What a row is called: the sailing name, else "Sailing #<id>", else "alert #<alert id>". */
export function alertDisplayName(name, cruiseId, alertId) {
  const n = typeof name === 'string' ? name.trim() : '';
  if (n) return n;
  if (Number.isInteger(cruiseId)) return `Sailing #${cruiseId}`;
  return `alert #${alertId}`;
}

/** Accessible name of a row's Cancel button. Starts with its visible text (WCAG 2.5.3). */
export function cancelAlertLabel(displayName, cancelling) {
  return `${cancelling ? 'Cancelling alert' : 'Cancel alert'} for ${displayName}`;
}

/**
 * Where focus goes after row `index` of a `count`-row list is removed (WCAG 2.4.3):
 * the next row's Cancel button (it now sits at the same index), the card heading after the last
 * row, or the empty-state line when the list is now empty.
 */
export function focusAfterRemoval(index, count) {
  if (!Number.isInteger(count) || count <= 1) return { target: 'empty' };
  if (Number.isInteger(index) && index >= 0 && index < count - 1) return { target: 'row', index };
  return { target: 'heading' };
}

/** Manage token from a location hash like "#t=abc", or null. */
export function manageTokenFromHash(hash) {
  if (typeof hash !== 'string' || hash.length < 2 || hash.length > 4096) return null;
  const body = hash.charAt(0) === '#' ? hash.slice(1) : hash;
  for (const part of body.split('&')) {
    const eq = part.indexOf('=');
    if (eq > 0 && part.slice(0, eq) === 't') {
      let value = part.slice(eq + 1);
      try { value = decodeURIComponent(value); } catch (e) { return null; }
      return /^[A-Za-z0-9._-]{10,2048}$/.test(value) ? value : null;
    }
  }
  return null;
}

/** Read the manage token kept for this tab (sessionStorage), or null. */
export function loadManageToken(storage) {
  try {
    const v = storage && storage.getItem(MANAGE_TOKEN_KEY);
    return typeof v === 'string' && v ? v : null;
  } catch (e) {
    return null;
  }
}

export function saveManageToken(storage, token) {
  try {
    if (token) storage.setItem(MANAGE_TOKEN_KEY, token);
    else storage.removeItem(MANAGE_TOKEN_KEY);
  } catch (e) {
    /* storage unavailable: the token still works for this page view */
  }
}

function read(storage) {
  try {
    const raw = storage && storage.getItem(MY_ALERTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function write(storage, entries) {
  try {
    storage.setItem(MY_ALERTS_KEY, JSON.stringify(entries.slice(0, MY_ALERTS_MAX)));
  } catch (e) {
    /* storage unavailable: the alert still exists and its email has a cancel link */
  }
}

function valid(entry, now) {
  return !!entry
    && Number.isInteger(entry.id)
    && typeof entry.token === 'string' && entry.token.length > 0
    && Number.isFinite(entry.created)
    && now - entry.created < MY_ALERTS_MAX_AGE_MS;
}

/** Entries still usable, newest first. Never includes an email address. */
export function loadMyAlerts(storage, now = Date.now()) {
  return read(storage).filter((e) => valid(e, now)).sort((a, b) => b.created - a.created);
}

/** Remember one created alert. Only id, cruise, target, cabin and token are kept. */
export function rememberAlert(storage, created, now = Date.now()) {
  if (!created || !Number.isInteger(created.id) || typeof created.token !== 'string' || !created.token) return;
  const entry = {
    id: created.id,
    cruiseId: Number.isInteger(created.cruiseId) ? created.cruiseId : null,
    targetPrice: Number.isFinite(created.targetPrice) ? created.targetPrice : null,
    cabinType: typeof created.cabinType === 'string' && created.cabinType ? created.cabinType : null,
    token: created.token,
    created: now,
  };
  const rest = loadMyAlerts(storage, now).filter((e) => e.id !== entry.id);
  write(storage, [entry, ...rest]);
}

export function forgetAlert(storage, id) {
  write(storage, read(storage).filter((e) => e && e.id !== id));
}
